<template>
  <div
    class="approve-success-icon"
    :class="{ 'approve-success-icon--static': reducedMotion }"
    role="img"
    aria-label="投运成功"
  >
    <span class="approve-success-icon__pulse approve-success-icon__pulse--a" aria-hidden="true" />
    <span class="approve-success-icon__pulse approve-success-icon__pulse--b" aria-hidden="true" />

    <div class="approve-success-icon__shell">
      <svg class="approve-success-icon__svg" viewBox="0 0 96 96" aria-hidden="true">
        <defs>
          <linearGradient :id="gradientId" x1="8%" y1="8%" x2="92%" y2="92%">
            <stop offset="0%" stop-color="var(--color-success)" />
            <stop offset="100%" stop-color="var(--color-primary)" />
          </linearGradient>
        </defs>

        <circle cx="48" cy="48" r="40" class="approve-success-icon__fill" />
        <circle
          cx="48"
          cy="48"
          r="40"
          class="approve-success-icon__ring"
          :stroke="`url(#${gradientId})`"
        />
        <path
          class="approve-success-icon__check"
          d="M32 49.5 42.5 60 64 38.5"
          pathLength="1"
        />
      </svg>

      <span class="approve-success-icon__corner" aria-hidden="true">
        <Zap :size="15" :stroke-width="2.5" />
      </span>
    </div>
  </div>
</template>

<script setup>
import { useId } from 'vue'
import { Zap } from 'lucide-vue-next'

defineProps({
  reducedMotion: {
    type: Boolean,
    default: false,
  },
})

const gradientId = `approve-success-ring-${useId().replace(/:/g, '')}`
</script>

<style scoped lang="scss">
.approve-success-icon {
  position: relative;
  width: 128px;
  height: 128px;
  margin: 0 auto 24px;
  animation: icon-enter 0.75s cubic-bezier(0.34, 1.35, 0.64, 1) 0.08s both;
}

.approve-success-icon__pulse {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  pointer-events: none;
}

.approve-success-icon__pulse--a {
  border: 2px solid color-mix(in srgb, var(--color-success) 45%, #fff);
  animation: icon-pulse 2.1s ease-out 2;
  animation-fill-mode: forwards;
}

.approve-success-icon__pulse--b {
  border: 2px solid color-mix(in srgb, var(--color-primary) 35%, #fff);
  animation: icon-pulse 2.1s ease-out 2;
  animation-delay: 0.55s;
  animation-fill-mode: forwards;
}

.approve-success-icon__shell {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #fff;
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--color-primary) 10%, #fff),
    0 10px 28px color-mix(in srgb, var(--color-primary) 14%, transparent),
    0 4px 12px rgba(8, 40, 87, 0.06);
}

.approve-success-icon__svg {
  width: 96px;
  height: 96px;
  display: block;
}

.approve-success-icon__fill {
  fill: #fff;
}

.approve-success-icon__ring {
  fill: none;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dasharray: 1;
  stroke-dashoffset: 1;
  transform-origin: 48px 48px;
  transform: rotate(-90deg);
  animation: ring-draw 0.85s cubic-bezier(0.4, 0, 0.2, 1) 0.12s forwards;
}

.approve-success-icon__check {
  fill: none;
  stroke: var(--color-success);
  stroke-width: 4.5;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 1;
  stroke-dashoffset: 1;
  animation: check-draw 0.55s cubic-bezier(0.65, 0, 0.35, 1) 0.55s forwards;
}

.approve-success-icon__corner {
  position: absolute;
  right: 6px;
  bottom: 6px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #fff;
  background: linear-gradient(145deg, var(--color-primary) 0%, color-mix(in srgb, var(--color-primary) 82%, #000) 100%);
  box-shadow: 0 4px 10px color-mix(in srgb, var(--color-primary) 28%, transparent);
  animation: corner-pop 0.45s cubic-bezier(0.34, 1.4, 0.64, 1) 0.9s both;
}

.approve-success-icon--static {
  animation: none;

  .approve-success-icon__pulse,
  .approve-success-icon__ring,
  .approve-success-icon__check,
  .approve-success-icon__corner {
    animation: none !important;
  }

  .approve-success-icon__ring,
  .approve-success-icon__check {
    stroke-dashoffset: 0;
  }
}

@keyframes icon-enter {
  0% {
    opacity: 0;
    transform: scale(0.82);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes icon-pulse {
  0% {
    transform: scale(0.92);
    opacity: 0.75;
  }
  100% {
    transform: scale(1.28);
    opacity: 0;
  }
}

@keyframes ring-draw {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes check-draw {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes corner-pop {
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .approve-success-icon:not(.approve-success-icon--static) {
    animation: none;
  }

  .approve-success-icon__pulse,
  .approve-success-icon__ring,
  .approve-success-icon__check,
  .approve-success-icon__corner {
    animation: none !important;
  }

  .approve-success-icon__ring,
  .approve-success-icon__check {
    stroke-dashoffset: 0;
  }
}
</style>
